import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  type CreateLeadSubmissionParams,
  type LeadSubmission,
} from "interfaces/lead-submission";
import {
  createLeadSubmission,
  getLeadSubmissionById,
} from "services/SauceService";
import {
  buildSelfCampaignSearchWithId,
  getSelfCampaignQueryParams,
} from "./queryParams";

const BASE_PATH = "https://www.mustard.love";
const PRICING_REDIRECT_PATH = `${BASE_PATH}/pricing`;
const WELCOME_REDIRECT_PATH = `${BASE_PATH}/welcome`;

type UseSelfCampaignLeadMode = "create" | "required";

type UseSelfCampaignLeadOptions = {
  mode: UseSelfCampaignLeadMode;
  shouldFetchLeadSubmission?: boolean;
};

type UseSelfCampaignLeadResult = {
  leadSubmission: LeadSubmission | null;
  leadSubmissionId: string | null;
  createdLeadSubmissionId: string | null;
  loadingLeadSubmission: boolean;
  queryParams: ReturnType<typeof getSelfCampaignQueryParams>;
};

type CreatedLeadSubmissionState = {
  id: string;
  createKey: string;
};

const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const createLeadSubmissionPromises = new Map<
  string,
  ReturnType<typeof createLeadSubmission>
>();

export const selfCampaignLeadNavigation = {
  redirect(path: string): void {
    window.location.assign(path);
  },
};

const hasBootstrapParams = (params: {
  email?: string;
  businessName?: string;
  phone?: string;
  partner?: string;
}): boolean => {
  return Boolean(params.email && params.businessName && params.phone);
};

const buildCreateLeadSubmissionPayload = (params: {
  email?: string;
  businessName?: string;
  phone?: string;
  partner?: string;
}): CreateLeadSubmissionParams => {
  return {
    businessName: params.businessName as string,
    businessEmail: params.email as string,
    businessPhone: params.phone as string,
    partner: params.partner as string,
  };
};

export const useSelfCampaignLead = ({
  mode,
  shouldFetchLeadSubmission = true,
}: UseSelfCampaignLeadOptions): UseSelfCampaignLeadResult => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => {
    return getSelfCampaignQueryParams(location.search);
  }, [location.search]);
  const { id, legacyLeadSubmissionId, businessName, email, phone, partner } =
    queryParams;

  const [leadSubmission, setLeadSubmission] = useState<LeadSubmission | null>(
    null,
  );
  const [createdLeadSubmission, setCreatedLeadSubmission] =
    useState<CreatedLeadSubmissionState | null>(null);
  const [loadingLeadSubmission, setLoadingLeadSubmission] =
    useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const resolveLeadSubmission = async () => {
      setLoadingLeadSubmission(true);
      setLeadSubmission(null);

      if (id && legacyLeadSubmissionId) {
        navigate(
          {
            pathname: location.pathname,
            search: buildSelfCampaignSearchWithId(location.search, id),
          },
          { replace: true },
        );
        return;
      }

      let nextLeadSubmissionId = id;
      if (!nextLeadSubmissionId) {
        if (mode !== "create") {
          selfCampaignLeadNavigation.redirect(WELCOME_REDIRECT_PATH);
          return;
        }

        if (!hasBootstrapParams({ email, businessName, phone })) {
          selfCampaignLeadNavigation.redirect(WELCOME_REDIRECT_PATH);
          return;
        }

        try {
          const createPayload = buildCreateLeadSubmissionPayload({
            businessName,
            email,
            partner,
            phone,
          });
          const createKey = JSON.stringify(createPayload);
          const existingCreatePromise =
            createLeadSubmissionPromises.get(createKey);
          const createPromise =
            existingCreatePromise || createLeadSubmission(createPayload);

          if (!existingCreatePromise) {
            createLeadSubmissionPromises.set(createKey, createPromise);
          }

          const createdLeadSubmission = await createPromise.finally(() => {
            createLeadSubmissionPromises.delete(createKey);
          });
          const createdLeadSubmissionId =
            asNonEmptyString(createdLeadSubmission?.id) ||
            asNonEmptyString(createdLeadSubmission?._id) ||
            asNonEmptyString(createdLeadSubmission?.leadSubmissionId);

          if (!createdLeadSubmissionId) {
            selfCampaignLeadNavigation.redirect(PRICING_REDIRECT_PATH);
            return;
          }

          if (!isMounted) {
            return;
          }

          setCreatedLeadSubmission({
            id: createdLeadSubmissionId,
            createKey,
          });

          navigate(
            {
              pathname: location.pathname,
              search: buildSelfCampaignSearchWithId(
                location.search,
                createdLeadSubmissionId,
              ),
            },
            { replace: true },
          );

          if (!shouldFetchLeadSubmission) {
            setLoadingLeadSubmission(false);
          }

          return;
        } catch (_error) {
          selfCampaignLeadNavigation.redirect(PRICING_REDIRECT_PATH);
          return;
        }
      }

      if (!shouldFetchLeadSubmission) {
        if (isMounted) {
          setLoadingLeadSubmission(false);
        }
        return;
      }

      try {
        const response = await getLeadSubmissionById(nextLeadSubmissionId);

        if (!isMounted) {
          return;
        }

        setLeadSubmission(response);
        setLoadingLeadSubmission(false);
      } catch (_error) {
        selfCampaignLeadNavigation.redirect(WELCOME_REDIRECT_PATH);
      }
    };

    void resolveLeadSubmission();

    return () => {
      isMounted = false;
    };
  }, [
    location.pathname,
    location.search,
    mode,
    navigate,
    businessName,
    email,
    id,
    legacyLeadSubmissionId,
    partner,
    phone,
    shouldFetchLeadSubmission,
  ]);

  const currentCreateKey = hasBootstrapParams({ email, businessName, phone })
    ? JSON.stringify(
        buildCreateLeadSubmissionPayload({
          businessName,
          email,
          partner,
          phone,
        }),
      )
    : null;
  const currentCreatedLeadSubmissionId =
    createdLeadSubmission &&
    (createdLeadSubmission.id === queryParams.id ||
      createdLeadSubmission.createKey === currentCreateKey)
      ? createdLeadSubmission.id
      : null;

  return {
    leadSubmission,
    leadSubmissionId: queryParams.id || currentCreatedLeadSubmissionId,
    createdLeadSubmissionId: currentCreatedLeadSubmissionId,
    loadingLeadSubmission,
    queryParams,
  };
};
